import FeaturedArticle from 'components/ArticleGrid/LargeFeaturedArticle';
import { cn, getColorway, getYMargins } from 'utils/styles';
import SmallArticle from 'components/ArticleGrid/SmallArticle';
import {
  ArticleGridProps,
  RelatedArticleFlexibleProps,
  RelatedArticleProps,
  ResourceProps,
} from 'libs/contentful/props';
import ArticleRowCarousel from 'components/ArticleGrid/ArticleRowCarousel';
import ArticleGridCarousel from 'components/ArticleGrid/ArticleGridCarousel';
import RichTextHeadline from 'components/shared/RichTextHeadline/RichTextHeadline';
import Button from 'components/shared/Buttons/BaseButton';
import MediumArticle from 'components/ArticleGrid/MediumArticle';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import { getRichTextValue } from '@/utils/formatters';
import { fetchEntriesByTaxonomyREST, adaptEntriesForArticleGrid, ArticleGridItem } from 'libs/contentful/rest';
import { isDraftPreviewEnabled } from 'utils/draftPreview';

/**
 * Fetches additional articles by taxonomy ID.
 * Supports PageBlogPostFlexible, BlogPostPage (blogPage), and Resource content types.
 * This is separated from the main component to keep the component testable.
 */
async function fetchArticlesByTaxonomy(taxonomyId: string): Promise<ArticleGridItem[]> {
  const draftPreview = await isDraftPreviewEnabled();
  const { entries, includes } = await fetchEntriesByTaxonomyREST(taxonomyId, draftPreview);

  if (entries.length > 0) {
    return adaptEntriesForArticleGrid(entries, includes);
  }
  return [];
}

export enum ArticleGridVariant {
  Article = 'Article',
  Featured = 'Featured',
  Explore = 'Explore',
  ShortCta = 'Short CTA',
  Grid = 'Grid',
}

const extractSlugFromUrl = (url: string | undefined): string => {
  if (!url) return '';

  try {
    // If it's a full URL, extract pathname + search params
    const urlObj = new URL(url);
    const pathAndSearch = urlObj.pathname + urlObj.search;
    // Remove leading slash
    return pathAndSearch.startsWith('/') ? pathAndSearch.slice(1) : pathAndSearch;
  } catch {
    // If it's not a valid URL, assume it's already a correctly formatted slug/path
    return url;
  }
};

const resourceToArticleShim = (
  item: ResourceProps | RelatedArticleProps | RelatedArticleFlexibleProps,
  taxonomyLabel?: string,
): RelatedArticleProps => {
  const getCategory = (defaultCategory?: { label: string }) => {
    if (taxonomyLabel) {
      return { label: taxonomyLabel };
    }
    return defaultCategory;
  };

  if (item.__typename === 'Resource') {
    const defaultCategory = item?.resourceLabelRte ? { label: getRichTextValue(item.resourceLabelRte) } : undefined;
    return {
      __typename: 'RelatedArticle',
      articleTitle: item.resourceCopy,
      articleImage: item.resourceIcon,
      slug: item.resourcePage?.slug || extractSlugFromUrl(item.url),
      category: getCategory(defaultCategory),
      sys: item?.sys || { id: '' },
    };
  }

  if (item.__typename === 'PageBlogPostFlexible') {
    return {
      __typename: 'RelatedArticle',
      articleTitle: item.articleTitle?.headline,
      articleImage: item.articleImage?.desktopMedia,
      slug: item.slug,
      category: getCategory(item.category),
      categories: item.categories,
      sys: item.sys,
    };
  }

  const relatedArticle = item as RelatedArticleProps;
  return {
    ...relatedArticle,
    category: getCategory(relatedArticle.category),
  };
};

/**
 * Props for the internal ArticleGridContent component.
 * Includes pre-fetched taxonomy articles if applicable.
 */
interface ArticleGridContentProps extends ArticleGridProps {
  /** Pre-fetched articles from taxonomy (already shimmed) */
  taxonomyArticles?: RelatedArticleProps[];
}

/**
 * Internal synchronous content component for ArticleGrid.
 */
const ArticleGridContent = ({
  variant,
  sectionName,
  color,
  margins,
  featuredArticleColor,
  articlesCollection,
  headline,
  cta,
  pullFromTaxonomy,
  taxonomyArticles = [],
  sys,
}: ArticleGridContentProps) => {
  // When taxonomy mode is active, use only taxonomy articles (no fallback to articlesCollection)
  // This ensures the component renders nothing when taxonomy is selected but returns no content
  const articles =
    pullFromTaxonomy?.value && pullFromTaxonomy?.label
      ? taxonomyArticles
      : articlesCollection.items.map(article => resourceToArticleShim(article));

  const useArticleGridCarouselLayout = variant === ArticleGridVariant.Explore;
  const useArticleLayout = variant === ArticleGridVariant.Article;
  const useFeaturedLayout = variant === ArticleGridVariant.Featured;
  const useShortCtaLayout = variant === ArticleGridVariant.ShortCta;
  const useGridLayout = variant === ArticleGridVariant.Grid;

  const threeArticleList = articles.slice(0, 3);

  if (!articles.length) return null;

  return (
    <section
      {...(sectionName != null && { id: encodeURIComponent(sectionName) })}
      className={cn(getColorway(color), getYMargins(margins))}>
      <div className="content-wrapper flex flex-col">
        {useGridLayout && (
          <>
            {headline && (
              <div
                className="font-sans font-semibold text-heading-md md:text-heading-lg mb-12"
                {...liveUpdateAttrs(sys.id, 'headline')}>
                <RichTextHeadline headline={headline} />
              </div>
            )}
            <div className="grid sm:grid-cols-2 md:grid-cols-3 grid-cols-1 gap-x-2 md:gap-x-8 gap-y-8">
              {articles.map((article, i) => (
                <SmallArticle {...article} articleImage={undefined} key={i} showFirstBorder />
              ))}
            </div>
          </>
        )}
        {useArticleLayout && (
          <ArticleRowCarousel color={color} totalItems={threeArticleList.length}>
            {threeArticleList.map((article, i) => (
              <MediumArticle {...article} key={i} />
            ))}
          </ArticleRowCarousel>
        )}

        {useFeaturedLayout && (
          <div className={cn('grid md:grid-cols-3 grid-cols-1 grid-rows-1 gap-8')}>
            <div className="col-span-2">
              <FeaturedArticle {...articles[0]} featuredArticleColor={featuredArticleColor} />
            </div>

            <div className="grid grid-rows-3 gap-12 items-center">
              {articles.slice(1, 4).map((article, i) => {
                return <SmallArticle {...article} key={i} />;
              })}
            </div>
          </div>
        )}

        {useArticleGridCarouselLayout && <ArticleGridCarousel color={color} articles={articles} />}

        {useShortCtaLayout && (
          <div className={'flex flex-col md:flex-row md:justify-between gap-8'}>
            <div className={'flex flex-col w-[314px] gap-5'}>
              {headline && (
                <div
                  className="font-sans font-semibold text-heading-md md:text-heading-lg"
                  {...liveUpdateAttrs(sys.id, 'headline')}>
                  <RichTextHeadline headline={headline} />
                </div>
              )}
              {cta && (
                <div className="hidden md:block max-w-max">
                  <Button href={cta?.url} sys={cta?.sys} internalPage={cta?.internalPage} variant={'outline'}>
                    {cta?.text}
                  </Button>
                </div>
              )}
            </div>
            <div className={'max-w-[990px]'}>
              <ArticleRowCarousel color={color} totalItems={threeArticleList.length}>
                {threeArticleList.map((article, i) => (
                  <SmallArticle {...article} key={i} />
                ))}
              </ArticleRowCarousel>
            </div>

            {cta && (
              <div className="block md:hidden max-w-max">
                <Button href={cta?.url} sys={cta?.sys} internalPage={cta?.internalPage} variant={'outline'}>
                  {cta?.text}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

/**
 * Async Server Component that fetches taxonomy articles and renders the grid.
 * Supports fetching PageBlogPostFlexible, BlogPostPage, and Resource entries by taxonomy.
 */
const ArticleGrid = async (props: ArticleGridProps) => {
  let taxonomyArticles: RelatedArticleProps[] = [];

  // Fetch articles by taxonomy and add them to the articles list
  if (props.pullFromTaxonomy?.value && props.pullFromTaxonomy?.label) {
    const fetchedArticles = await fetchArticlesByTaxonomy(props.pullFromTaxonomy.value);
    const taxonomyLabel = props.pullFromTaxonomy.label;
    taxonomyArticles = fetchedArticles.map(article => resourceToArticleShim(article, taxonomyLabel));
  }

  return <ArticleGridContent {...props} taxonomyArticles={taxonomyArticles} />;
};

export default ArticleGrid;
